import React, { useState, useEffect } from 'react'
import { MigrantContext } from '../../../contexts/migrantContext'
import { Consumer } from '../../../models/Consumer'
import { StoreKey, OrderStatus, PaymentMethod } from '../../../config/enums'
import { Item, Variant, DeliveryOption } from '../../../models/Item'
import { Category } from '../../../models/Category'
import { Country } from '../../../models/Country'
import { Cart, CartItem } from '../../../models/Cart'
import { Gift } from '../../../models/Gift'
import constants from '../../../config/constants'
import { updateCartItems, updateOrder } from '../../../services/orderService'
import { Recipient } from '../../../models/Recipient'
import { markOrderAsPlaced } from '../../../services/paymentService'
import { fetchUserCredits } from '../../../services/creditService'
import { Credit } from '../../../models/Credit'

const MigrantContextProvider: React.FC = ({ children }) => {
  //#region States
  const [profile, setProfile] = useState<Consumer | null>(() => {
    const data = localStorage.getItem(StoreKey.migrantProfile)
    if (data !== null) {
      return JSON.parse(data) as Consumer
    }
    return null
  })

  const [shopCountry, setShopCountry] = useState<Country | null>(() => {
    const data = localStorage.getItem(StoreKey.shopCountry)
    if (data !== null) {
      return JSON.parse(data) as Country
    }
    return null
  })

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    () => {
      const data = localStorage.getItem(StoreKey.selectedCategory)
      if (data !== null) {
        return JSON.parse(data) as Category
      }
      return null
    }
  )

  const [cart, setCart] = useState<Cart>(() => {
    const data = localStorage.getItem(StoreKey.cart)
    if (data !== null) {
      return JSON.parse(data) as Cart
    } else {
      return { isCheckedOut: false, items: [] }
    }
  })

  const [isShoppingForSelf, setIsShoppingForSelf] = useState(false)
  const [credits, setCredits] = useState<Credit | null>(null)
  //#endregion

  //#region Functions
  const addItemToCart = (
    item: Item,
    quantity: number,
    variant: Variant,
    gift?: Gift,
    months?: number
  ) => {
    let exists = false
    cart.items.forEach(cartItem => {
      if (cartItem.item.id === item.id) {
        exists = true
      }
    })

    if (exists) {
      throw new Error(constants.alertError.shop.alreadyInCart)
    }

    const cartItem: CartItem = {
      item,
      quantity,
      variant,
      gift: gift
    }

    let cartCopy = { ...cart, items: [...cart.items, cartItem] }
    if (months) {
      cartCopy.months = months
    }

    return updateCart(cartCopy)
  }

  const removeItemFromCart = (cartItem: CartItem) => {
    const cartItemsCopy = [...cart.items]
    const i = cartItemsCopy.indexOf(cartItem)

    if (i >= 0) {
      cartItemsCopy.splice(i, 1)
      console.log('cartItemsCopy:', cartItemsCopy)
      const cartCopy = { ...cart, items: cartItemsCopy }

      if (cartItemsCopy.length === 0 && cartCopy.months) {
        delete cartCopy.months
      }

      return updateCart(cartCopy)
    } else {
      return Promise.resolve()
    }
  }

  const resetCachedCart = () => {
    setCart({
      isCheckedOut: false,
      items: [],
      order: undefined
    })
  }

  const updateCart = async (cartCopy: Cart) => {
    if (!cartCopy.items.length && !cartCopy.order) {
      return
    }

    try {
      const updatedOrder = await updateCartItems(cartCopy)

      setCart({
        ...cartCopy,
        order: updatedOrder
      })
    } catch (err) {
      console.error(err)
    }
  }

  const resyncCart = async () => {
    return updateCart(cart)
  }

  const updateOrderRecipient = async (recipient: Recipient) => {
    if (!cart.order) {
      throw new Error(constants.alertError.shop.orderNotReady)
    }

    const updatedOrder = await updateOrder({ ...cart.order, recipient })

    setCart({
      ...cart,
      order: updatedOrder
    })
  }

  const updateOrderShippingInfo = async (
    shippingInfo: { itemId: string; option: DeliveryOption }[],
    recipient?: Recipient
  ) => {
    if (!cart.order) {
      throw new Error(constants.alertError.shop.orderNotReady)
    }

    const orderItemsCopy = [...cart.order.orderItems]
    orderItemsCopy.forEach(orderItem => {
      const filtered = shippingInfo.filter(s => s.itemId === orderItem.item.id)
      if (filtered.length) {
        let minDate = new Date()
        let maxDate = new Date()
        minDate.setDate(minDate.getDate() + filtered[0].option.minDays)
        maxDate.setDate(maxDate.getDate() + filtered[0].option.maxDays)

        orderItem.shippingInfo = {
          courier: filtered[0].option.courier,
          fee: filtered[0].option.fee,
          minArrivalDate: minDate,
          maxArrivalDate: maxDate
        }
      }
    })

    const orderCopy = {
      ...cart.order,
      orderItems: orderItemsCopy
    }
    if (recipient) {
      orderCopy.recipient = recipient
    }

    const updatedOrder = await updateOrder(orderCopy, cart.months)

    setCart({
      ...cart,
      order: updatedOrder
    })
  }

  const placeOrder = async (paymentMethod: PaymentMethod) => {
    if (!cart.order) {
      throw new Error(constants.alertError.shop.orderNotReady)
    }

    if (paymentMethod !== PaymentMethod.hoolah) {
      // Only call placeOrder func if payMethod is not hoolah
      await markOrderAsPlaced(cart.order.id, paymentMethod)
    }

    setCart({
      ...cart,
      order: {
        ...cart.order,
        status: OrderStatus.placed,
        paymentMethod
      }
    })
  }

  const updateOrderStatus = (status: OrderStatus) => {
    if (!cart.order) {
      throw new Error(constants.alertError.shop.orderNotReady)
    }

    setCart({
      ...cart,
      order: {
        ...cart.order,
        status
      }
    })
  }
  //#endregion

  //#region useEffect declations
  useEffect(() => {
    localStorage.setItem(StoreKey.migrantProfile, JSON.stringify(profile))

    if (profile?.id && profile?.id !== '') {
      // Listen to user credits changes from firestore
      fetchUserCredits(profile?.id, freshCredits => {
        setCredits(freshCredits)
      }).then(unsubscribe => {
        return unsubscribe
      })
    }
  }, [profile])

  useEffect(() => {
    localStorage.setItem(StoreKey.shopCountry, JSON.stringify(shopCountry))
  }, [shopCountry])

  useEffect(() => {
    if (shopCountry?.id === profile?.workCountry?.id) {
      setIsShoppingForSelf(true)
    } else {
      setIsShoppingForSelf(false)
    }
  }, [shopCountry, profile])

  useEffect(() => {
    localStorage.setItem(
      StoreKey.selectedCategory,
      JSON.stringify(selectedCategory)
    )
  }, [selectedCategory])

  useEffect(() => {
    localStorage.setItem(StoreKey.cart, JSON.stringify(cart))
  }, [cart])
  //#endregion

  return (
    <MigrantContext.Provider
      value={{
        profile,
        setProfile,
        shopCountry,
        setShopCountry,
        isShoppingForSelf,
        selectedCategory,
        setSelectedCategory,
        cart,
        addItemToCart,
        removeItemFromCart,
        resetCachedCart,
        resyncCart,
        updateOrderRecipient,
        updateOrderShippingInfo,
        placeOrder,
        updateOrderStatus,
        credits
      }}
    >
      {children}
    </MigrantContext.Provider>
  )
}

export default MigrantContextProvider
